import React from 'react';
import {emailRegex, preSignupFilesExpiry} from '@/constants';
import {PRE_SIGNUP_FILES_KEY, SIGNUP_LICENSE_FILE_KEY} from '@/constants/keys';
import {clsx, type ClassValue} from 'clsx';
import {twMerge} from 'tailwind-merge';
import {FileSerializableItem, PreSignupFileSerializableItem, SelectInputitem} from '@/interface';
import {screenSize} from '@/theme';

export function resetMessage(cb: () => void) {
  setTimeout(cb, 500);
}

export function maskEmail(email: string, allowLength: boolean = true) {
  if (emailRegex.test(email)) {
    const [host, rest] = email.split('@');
    const length = host.length;
    if (length === 1) {
      return null;
    }
    if (length === 2) {
      return `${host[0]}*@${rest}`;
    }
    if (length === 3) {
      return `${host[0]}*${host[2]}@${rest}`;
    }
    if (length === 4) {
      return `${host[0]}**${host[3]}@${rest}`;
    }
    const l_chars = 2;
    const r_chars = 2;

    const l_part = email.slice(0, l_chars);
    const r_part = email.slice(length - r_chars, length);

    const remaning_length = host.length - (l_chars + r_chars);
    return `${l_part}${'*'.repeat(allowLength ? remaning_length : 4)}${r_part}@${rest}`;
  }
  return null;
}

export function maskPhone(phone: string) {
  if (!phone || typeof phone !== 'string') {
    return '*******';
  }

  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');

  if (digitsOnly.length === 0) {
    return '*******';
  }

  // If phone number is 4 digits or less, mask all but last digit
  if (digitsOnly.length <= 4) {
    return '*'.repeat(Math.max(1, digitsOnly.length - 1)) + digitsOnly.slice(-1);
  }

  // Show last 4 digits, mask the rest
  const visibleDigits = 4;
  const maskedLength = digitsOnly.length - visibleDigits;
  return '*'.repeat(maskedLength) + digitsOnly.slice(-visibleDigits);
}

/**
 * This function is used to extract the initial from any given name
 * @example
 *  "John Doe" -> "JD"
 *  "Mary Jane Watson" -> "MJ"
 * @param name name of the user
 * @returns initals for the name
 */
export function getInitials(name: string): string {
  if (!name) return '';

  // Split by spaces and filter out empty strings
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) return '';

  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }

  const firstInitial = words[0][0].toUpperCase();
  const lastInitial = words[words.length - 1][0].toUpperCase();

  return `${firstInitial}${lastInitial}`;
}

/**
 * Capitalizes the first letter of each word in a name.
 *
 * @param name - The input string (e.g., "john doe").
 * @returns The formatted string (e.g., "John Doe").
 */
export function formatName(name: string, trim: boolean = true): string {
  if (trim && !name?.trim()) {
    return '';
  }

  if (trim) {
    name = name.trim();
  }

  return name
    .split(/\s+/) // split by one or more spaces
    .map(
      word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(), // capitalize first, lowercase rest
    )
    .join(' ');
}

/**
 * Creates a blur handler that formats the input value with capitalized first letters.
 * Use this for name, designation, education, and similar text fields.
 *
 * @param handleBlur - Formik's handleBlur function
 * @param setFieldValue - Formik's setFieldValue function
 * @param fieldName - The name of the field to update
 * @returns A blur event handler function
 */
export function createFormattedBlurHandler(
  handleBlur: (e: React.FocusEvent<any>) => void,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  fieldName: string,
): (e: React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLTextAreaElement>) => void {
  return (e: React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLTextAreaElement>) => {
    handleBlur(e);
    // Format value with capitalized first letters when user leaves the field
    const currentValue = (e.target as HTMLInputElement).value;
    if (currentValue?.trim()) {
      setFieldValue(fieldName, formatName(currentValue));
    }
  };
}

export function formatConsultationDate(dateStr: string, format: 'long' | 'short' = 'long', dateSep = '-') {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';

  const day = date.getDate().toString().padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  if (format === 'long') {
    return `${day}${dateSep}${month}${dateSep}${year}, ${hours}:${minutes} ${ampm}`;
  } else {
    return `${day}${dateSep}${month}${dateSep}${year}`;
  }
}

export function formatDateTime(dateStr: string) {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';

  const day = date.getDate();
  const month = date.toLocaleString('en-US', {month: 'short'});
  const hour = date.getHours() % 12 || 12;
  const period = date.getHours() >= 12 ? 'PM' : 'AM';

  return `${day} ${month}, ${hour} ${period}`;
}

export function formatCurrency(value: number){
  return Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

export const timeRemaining = (deadlineStr: string) => {
  const now = new Date();
  const deadline = new Date(deadlineStr);

  // Validate deadline
  if (isNaN(deadline.getTime())) {
    return 'Invalid date';
  }

  const diffMs = deadline.getTime() - now.getTime();

  if (diffMs <= 0) return '0m Remaining'; // if past deadline

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let timeString = '';

  if (hours > 0) {
    timeString += `${hours}h`;
    if (minutes > 0) timeString += ` ${minutes}m`;
  } else {
    timeString += `${minutes}m`;
  }

  return `${timeString} Remaining`;
};

/**
 * Calculate Turnaround Time (TAT) and format it as hours or minutes
 * @param startDate - ISO date string (e.g., "2026-02-23T11:04:29.556032+01:00")
 * @param endDate - ISO date string (e.g., "2026-02-24T11:46:00.217540+01:00") or undefined
 * @returns Formatted string like "12 Hrs" or "15 Min", or "0 Min" if endDate is not provided or invalid
 */
export const calculateTATHours = (startDate: string, endDate?: string): string => {
  if (!endDate) {
    return '0 Min';
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  // Validate dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return '0 Min';
  }

  const diffMs = end.getTime() - start.getTime();

  if (diffMs <= 0) return '0 Min';

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return hours === 1 ? '1 Hr' : `${hours} Hrs`;
  } else {
    return `${minutes} Min`;
  }
};

/**
 * @param str string to check
 * @param count counter number
 * @returns string with 's' appended if it is not already plural
 */
export function appendPlural(str: string, count: number): string {
  if (count === 1) {
    return str;
  }
  return `${str}s`;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function fileFromObjectURL(objectUrl: string, fileName: string): Promise<File> {
  try {
    const response = await fetch(objectUrl);
    const blob = await response.blob();
    return new File([blob], fileName, {type: blob.type});
  } catch (error) {
    return new File(['test'], fileName, {type: 'application/octet-steam'});
  }
}

/**
 * Convert a TypeScript enum to SelectInputitem array
 * Handles both numeric and string enums
 * @param enumObject - The enum to convert
 * @param startId - Starting ID for options (default: 1)
 * @returns Array of SelectInputitem
 */
export const enumToSelectOptions = (
  enumObject: Record<string, string | number>,
  labelMapping: Record<string, string> = {},
  startId: number = 1,
): SelectInputitem[] => {
  return Object.entries(enumObject)
    .filter(([key]) => !isNaN(Number(key)))
    .map(([key, value], index) => ({
      id: startId + index,
      label: labelMapping[key] || String(value),
    }));
};

export function ageFromDate(dob: string | null, numberOnly: boolean = false) {
  if (!dob) return null;

  const userDob = new Date(dob);
  const currentDate = new Date();

  const diffInMs = currentDate.getTime() - userDob.getTime();

  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
  const diffInMonths = diffInDays / 30.44; // average month length
  const diffInYears = diffInDays / 365.25; // average year length

  if (diffInYears < 1) {
    if (diffInMonths < 1) {
      const days = Math.floor(diffInDays);
      return `${days} ${appendPlural('day', days)}`;
    } else {
      const months = Math.floor(diffInMonths);
      return `${months} ${appendPlural('month', months)}`;
    }
  } else {
    const years = Math.floor(diffInYears);
    return numberOnly ? years : `${years} ${appendPlural('year', years)}`;
  }
}

export function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  }
  return `${(size / 1024 / 1024).toFixed(2)} MB`;
}

export function getPreSignupFilesToken(): string | null {
  return localStorage.getItem(PRE_SIGNUP_FILES_KEY) || null;
}

export function clearPreSignupFilesToken() {
  localStorage.removeItem(PRE_SIGNUP_FILES_KEY);
}

export function clearSignupLicenseFile(){
  localStorage.removeItem(SIGNUP_LICENSE_FILE_KEY);
}

export function savePreSignupFilesToken(token: string) {
  localStorage.setItem(PRE_SIGNUP_FILES_KEY, token);
}

export function downloadFileFromUrl(url: string, filename?: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename ?? '';
  anchor.style.display = 'none';

  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export async function fetchAndDownloadFile(url: string, filename?: string): Promise<void> {
  const response = await fetch(url);
  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename ?? '';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(objectUrl);
}

export function matchesRoute(pathname: string, template: string): boolean {
  const pathSegments = pathname.split('/').filter(Boolean);
  const templateSegments = template.split('/').filter(Boolean);

  if (pathSegments.length !== templateSegments.length) {
    return false;
  }

  return templateSegments.every((segment, index) => {
    // Dynamic segments (starting with :) match any value
    if (segment.startsWith(':')) {
      return true;
    }
    // Static segments must match exactly
    return segment === pathSegments[index];
  });
}

export function splitAndHighlight(text: string, highlights: string[]): {text: string; highlighted: boolean}[] {
  if (!text) return [];

  // Escape regex special chars in highlight words
  const escapeRegex = (str: string) => String(str).replaceAll(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Build regex: (how|word2|word3)
  const pattern = highlights.map(escapeRegex).join('|');
  const regex = new RegExp(`(${pattern})`, 'gi');

  const parts = text.split(regex);

  return parts
    .filter(p => p.trim() !== '' || p.length > 0)
    .map(part => ({
      text: part,
      highlighted: highlights.some(h => h.toLowerCase() === part.toLowerCase()),
    }));
}

export function formatMessageTime(time: string) {
  const date = new Date(time);
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');

  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${ampm}`;
}

// ===========================
// JSON Diff Utility
// ===========================
// Helper to check if value is an object (not string, null, array, or primitive)
const isObject = (value: any): boolean => {
  return value !== null && typeof value === 'object' && !Array.isArray(value) && typeof value !== 'string';
};

// Helper to format value for display
const formatValue = (value: any): string => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return JSON.stringify(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// Helper to format key label (convert snake_case to Title Case)
const formatKeyLabel = (key: string): string => {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface DiffResult {
  type: 'added' | 'removed' | 'updated';
  oldVal?: any;
  newVal?: any;
}

// Recursive diff algorithm - only returns changed fields
const computeDiff = (oldObj: any, newObj: any): Record<string, DiffResult> => {
  const diff: Record<string, DiffResult> = {};
  const oldKeys = Object.keys(oldObj || {});
  const newKeys = Object.keys(newObj || {});
  const allKeys = new Set([...oldKeys, ...newKeys]);

  allKeys.forEach(key => {
    const oldVal = oldObj?.[key];
    const newVal = newObj?.[key];
    const hasOld = key in (oldObj || {});
    const hasNew = key in (newObj || {});

    if (!hasOld && hasNew) {
      // Added
      diff[key] = {type: 'added', newVal};
    } else if (hasOld && !hasNew) {
      // Removed
      diff[key] = {type: 'removed', oldVal};
    } else if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      // Updated - only if actually different
      diff[key] = {type: 'updated', oldVal, newVal};
    }
    // Skip unchanged values
  });

  return diff;
};

export const formatJsonWithDiff = (oldVal: any, newVal: any): React.ReactElement[] => {
  const parseValue = (val: any): any => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'object') return val;
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch {
        return val;
      }
    }
    return val;
  };

  const oldParsed = parseValue(oldVal);
  const newParsed = parseValue(newVal);

  // If not both objects, return simple display
  if (!isObject(oldParsed) || !isObject(newParsed)) {
    const formatted = typeof newParsed === 'string' ? newParsed : JSON.stringify(newParsed, null, 2);
    return [React.createElement('span', {key: 'single'}, formatted)];
  }

  const diff = computeDiff(oldParsed, newParsed);
  const resultElements: React.ReactElement[] = [];

  Object.entries(diff).forEach(([key, diffResult]) => {
    const label = formatKeyLabel(key);
    const children: React.ReactElement[] = [
      React.createElement('span', {key: 'label', className: 'font-FigtreeSemiBold text-sm text-ui_input_border_dark'}, `${label}:`),
    ];

    if (diffResult.type === 'removed' && diffResult.oldVal !== undefined) {
      children.push(
        React.createElement(
          'span',
          {
            key: 'old',
            className: 'px-2 py-1 rounded-md text-sm font-FigtreeSemiBold',
            style: {backgroundColor: '#FEE2E2', color: '#FF3737'},
          },
          formatValue(diffResult.oldVal),
        ),
      );
    }

    if ((diffResult.type === 'removed' || diffResult.type === 'updated') && diffResult.oldVal !== undefined) {
      children.push(React.createElement('span', {key: 'arrow', className: 'text-ui_input_border_dark'}, '→'));
    }

    if ((diffResult.type === 'added' || diffResult.type === 'updated') && diffResult.newVal !== undefined) {
      children.push(
        React.createElement(
          'span',
          {
            key: 'new',
            className: 'px-2 py-1 rounded-md text-sm font-FigtreeSemiBold',
            style: {backgroundColor: '#D1FAE5', color: '#198F51'},
          },
          formatValue(diffResult.newVal),
        ),
      );
    }

    resultElements.push(
      React.createElement('div', {key, className: 'mb-3'}, React.createElement('div', {className: 'flex items-center gap-2 flex-wrap'}, children as any)),
    );
  });

  return resultElements.length > 0 ? resultElements : [React.createElement('span', {key: 'no-changes'}, 'No changes')];
};

export function positionChatPanel({
  buttonRect,
  chatPanelRef,
  margin_y = 10,
  margin_x = 50,
}: {
  buttonRect: any;
  chatPanelRef: any;
  margin_y?: number;
  margin_x?: number;
}) {
  if (buttonRect) {
    let {top} = buttonRect;
    if (chatPanelRef.current) {
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      let panelWidth = Math.min(viewportWidth * 0.95, 300);
      let panelHeight = 400;
      if (viewportWidth > screenSize.md) {
        panelHeight = 500;
        panelWidth = 400;
      }
      if (viewportWidth > screenSize.xl) {
        panelHeight = 600;
        panelWidth = 500;
      }

      // make sure to not exceed the max height of the page
      if (top + panelHeight > window.innerHeight) {
        top = window.innerHeight - panelHeight - margin_y;
      }

      chatPanelRef.current.style.top = `${top}px`;
      chatPanelRef.current.style.right = `${margin_x}px`;
    }
  }
}
